import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Tank's backstory
const TANK_BACKSTORY = {
  name: "Tank",
  realName: "Marcus 'Tank' Thompson",
  occupation: "Long-haul truck driver & VeriWatch AI Specialist",
  bio: `Former Army mechanic turned trucker, Tank has logged over 2 million miles 
across all 48 continental states. His 18-wheeler doubles as a mobile command center, 
equipped with HAM radio, solar panels, and a 6-month food supply.

After witnessing the 2021 Texas grid collapse firsthand while delivering emergency 
supplies, Tank became obsessed with preparedness. He now spends his off-hours 
monitoring OSINT feeds, running readiness drills with his local prepper network, 
and training VeriWatch's AI systems with real-world threat scenarios.

"The road teaches you one thing: always be ready for what's around the next bend."

Specialties: Supply chain logistics, grid-down scenarios, vehicle survival, 
long-range communication, and threat pattern recognition.`,
  funFacts: [
    "Keeps a bug-out bag rated for -40°F in his cab at all times",
    "Can hot-wire any vehicle made before 2010",
    "Runs a popular CB channel for truckers sharing road hazard intel",
    "His callsign is 'Nomad-7' on the prepper radio network"
  ]
};

const TankAvatar = ({ 
  size = 'md', 
  showTooltip = true, 
  className = '',
  showName = false 
}) => {
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-24 h-24'
  };

  const avatar = (
    <div className={`relative ${className}`}>
      <img 
        src="/images/tank.png" 
        alt="Tank - VeriWatch AI" 
        className={`${sizeClasses[size]} rounded-full object-cover border-2 border-teal-500/50 shadow-lg shadow-teal-500/20`}
      />
      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900" />
      {showName && (
        <span className="ml-2 text-sm font-medium text-teal-400">Tank</span>
      )}
    </div>
  );

  if (!showTooltip) {
    return avatar;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div className="cursor-help inline-flex items-center">
            {avatar}
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="max-w-md p-4 bg-slate-900 border border-teal-500/30 text-slate-200 shadow-xl"
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3 border-b border-slate-700 pb-3">
              <img 
                src="/images/tank.png" 
                alt="Tank" 
                className="w-14 h-14 rounded-full border-2 border-teal-500"
              />
              <div>
                <h4 className="font-bold text-teal-400 text-lg">{TANK_BACKSTORY.name}</h4>
                <p className="text-xs text-slate-400">{TANK_BACKSTORY.realName}</p>
                <p className="text-xs text-amber-400">{TANK_BACKSTORY.occupation}</p>
              </div>
            </div>
            
            <p className="text-sm text-slate-300 whitespace-pre-line leading-relaxed">
              {TANK_BACKSTORY.bio}
            </p>
            
            <div className="pt-2 border-t border-slate-700">
              <p className="text-xs text-teal-500 font-semibold mb-1">Fun Facts:</p>
              <ul className="text-xs text-slate-400 space-y-1">
                {TANK_BACKSTORY.funFacts.map((fact, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-amber-500">•</span> {fact}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Export both the component and the backstory for use elsewhere
export { TANK_BACKSTORY };
export default TankAvatar;
